import { useEffect, useRef } from 'react'

import { Check } from 'lucide-react'
import { useInView } from 'motion/react'

import { track } from '@/api/tracking'
import { ASSETS, FOOD_COST_LANDING_PAGE } from '@/components/food-cost/constants'

import { VIAS_LANDING_VARIANT } from './offerPaths'

const WITHIN_REACH_POINTS = [
    {
        id: 'acompanamiento',
        title: 'Alguien contigo, no plantillas a medias',
        body: 'Nada de trabajos eternos en plantillas que se quedan a medias: tienes una persona acompañándote y formándote para entender el valor de cada acción.',
    },
    {
        id: 'foco',
        title: 'Te devolvemos a lo que importa',
        body: 'Cuando vengan otros problemas que te quieran sacar de poner remedio, te devolvemos a donde te importa ahora mismo: arreglar tus números.',
    },
    {
        id: 'tiempo',
        title: 'Ponerlo en marcha y mantenerlo sencillo',
        body: 'Pagas por ponerlo en marcha y que luego sea sencillo de mantener al día. El tiempo es oro en este sector.',
    },
    {
        id: 'herramienta',
        title: 'Herramienta hecha para tu negocio',
        body: 'Todo acompañado por la herramienta, adaptada para usarse desde dentro de tu negocio y no en despachos.',
    },
] as const

export function ViasWhatIsTipiSection() {
    return (
        <section className='bg-[#F5EEE4] py-10 md:py-16' aria-labelledby='vias-what-is-tipi-heading'>
            <div className='mx-auto grid w-full max-w-[1180px] grid-cols-1 items-center gap-6 px-[22px] md:grid-cols-[minmax(0,1.15fr)_minmax(240px,0.85fr)] md:gap-12 md:px-10 lg:gap-16'>
                <div className='flex flex-col gap-5 md:gap-6'>
                    <div className='flex items-center gap-3 md:gap-4'>
                        <img
                            src={ASSETS.whatIsTipiCharacter}
                            alt=''
                            width={96}
                            height={104}
                            loading='lazy'
                            decoding='async'
                            className='h-14 w-auto shrink-0 md:hidden'
                            aria-hidden
                        />
                        <h2
                            id='vias-what-is-tipi-heading'
                            className='m-0 text-[28px] font-medium leading-[1.15] tracking-[-0.8px] text-[#F4502A] md:text-[36px] lg:text-[42px]'
                        >
                            ¿Qué es Tipi?
                        </h2>
                    </div>

                    <div className='flex flex-col gap-3.5 md:gap-4'>
                        <p className='m-0 max-w-[52ch] text-[15px] leading-[1.65] text-[#5C5548] md:text-[17px] md:leading-[1.7]'>
                            Tipi nació para acompañar al pequeño hostelero y que la gestión de su negocio no se
                            convierta en una carga. Por eso no somos solo una herramienta: tienes un equipo
                            especializado contigo para ponértelo fácil.
                        </p>
                        <p className='m-0 max-w-[52ch] text-[15px] leading-[1.65] text-[#5C5548] md:text-[17px] md:leading-[1.7]'>
                            Está pensado para el hostelero pequeño, uno o dos locales, que no se puede permitir ni una
                            herramienta de administrativos ni un administrativo en nómina para llevar sus números.
                        </p>
                    </div>
                </div>

                <img
                    src={ASSETS.whatIsTipiCharacter}
                    alt=''
                    width={424}
                    height={512}
                    loading='lazy'
                    decoding='async'
                    className='mx-auto hidden h-auto w-full max-w-[280px] md:block lg:max-w-[320px]'
                    aria-hidden
                />
            </div>
        </section>
    )
}

export function ViasWhyWithinReachSection() {
    const sectionRef = useRef<HTMLElement>(null)
    const isInView = useInView(sectionRef, { once: true })

    useEffect(() => {
        if (!isInView) return
        track('How It Works', {
            landing_page: FOOD_COST_LANDING_PAGE,
            landing_variant: VIAS_LANDING_VARIANT,
        })
    }, [isInView])

    return (
        <section ref={sectionRef} className='bg-white py-[60px] md:py-20'>
            <div className='mx-auto max-w-[1180px] px-[22px] md:px-10'>
                <div className='flex flex-col gap-2.5'>
                    <h2 className='m-0 text-[32px] font-medium leading-[1.15] tracking-[-1px] text-[#F4502A] md:text-[46px]'>
                        ¿Por qué con Tipi sí estará a mi alcance?
                    </h2>
                    <p className='m-0 max-w-[58ch] text-[15px] leading-[1.65] text-[#5C5548] md:text-[17px] md:leading-[1.7]'>
                        Porque el acompañamiento es lo primero: la herramienta ayuda, pero no te deja solo con ella.
                    </p>
                </div>

                <div className='mt-10 grid grid-cols-1 gap-0 md:grid-cols-2 md:gap-x-10'>
                    {WITHIN_REACH_POINTS.map((point, index) => (
                        <div
                            key={point.id}
                            className='grid grid-cols-[44px_1fr] gap-4 border-t border-[#E8E1D5] py-[22px]'
                        >
                            <div className='text-[26px] font-medium text-[#8A7F6F]'>
                                {String(index + 1).padStart(2, '0')}
                            </div>
                            <div className='flex flex-col gap-1.5'>
                                <div className='flex items-start gap-2.5'>
                                    <Check
                                        className='mt-1 h-[18px] w-[18px] shrink-0 text-[#F4502A]'
                                        strokeWidth={2.75}
                                        aria-hidden
                                    />
                                    <h3 className='m-0 text-lg font-semibold text-[#17130E]'>{point.title}</h3>
                                </div>
                                <p className='m-0 text-[15px] leading-relaxed text-[#5C5548]'>{point.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}
